/**
 * Mock assessment service — backed by the in-memory `mockAssessments` fixtures.
 *
 * Returns copies and simulates latency. `create`/`update`/`updateStatus`
 * synthesize a response object (no shared-array mutation) — the page owns the
 * React state and re-reads after each mutation.
 */

import { mockAssessmentsForCustomer, mockAssessments } from '@/data/mockAssessments'
import { mockDelay } from '@/services/factory'
import type { AssessmentService, AuthorizedAssessmentDetail } from './assessments.service'
import type {
  Assessment,
  CreateAssessmentInput,
  UpdateAssessmentInput,
  AssessmentStatus,
  TaxCalculationInput,
  TaxCalculationResult,
  TaxRegime,
  AssessmentDocument,
  LinkAssessmentDocumentInput,
} from '@/types/assessment.types'

const mockAssessmentDocuments: AssessmentDocument[] = []

/** New-regime FY2024-25 slab calculation (simplified) for mock parity. */
function computeTax(
  grossIncome: number,
  totalDeductions: number,
  regime: TaxRegime
): Omit<TaxCalculationResult, 'assessmentYear' | 'taxRegime'> {
  const taxableIncome = Math.max(0, grossIncome - (regime === 'old' ? totalDeductions : 0))

  // Simplified new-regime slabs (FY 2024-25): 0/3L: 0, 3-6L: 5%, 6-9L: 10%,
  // 9-12L: 15%, 12-15L: 20%, 15L+: 30%. Old regime uses a flatter approximation.
  let taxOnIncome = 0
  if (regime === 'new') {
    const slabs: [number, number][] = [
      [300000, 0],
      [300000, 0.05],
      [300000, 0.1],
      [300000, 0.15],
      [300000, 0.2],
      [Infinity, 0.3],
    ]
    let remaining = taxableIncome
    for (const [width, rate] of slabs) {
      if (remaining <= 0) break
      const chunk = Math.min(remaining, width)
      taxOnIncome += chunk * rate
      remaining -= chunk
    }
    // Section 87A rebate for taxable income up to 7L.
    if (taxableIncome <= 700000) taxOnIncome = 0
  } else {
    const slabs: [number, number][] = [
      [250000, 0],
      [250000, 0.05],
      [500000, 0.2],
      [Infinity, 0.3],
    ]
    let remaining = taxableIncome
    for (const [width, rate] of slabs) {
      if (remaining <= 0) break
      const chunk = Math.min(remaining, width)
      taxOnIncome += chunk * rate
      remaining -= chunk
    }
    if (taxableIncome <= 500000) taxOnIncome = 0
  }

  const surcharge = taxableIncome > 5000000 ? taxOnIncome * 0.1 : 0
  const educationCess = (taxOnIncome + surcharge) * 0.04
  const totalTaxLiability = Math.round(taxOnIncome + surcharge + educationCess)

  return {
    grossIncome,
    totalDeductions,
    taxableIncome,
    taxOnIncome: Math.round(taxOnIncome),
    surcharge: Math.round(surcharge),
    educationCess: Math.round(educationCess),
    totalTaxLiability,
  }
}

export function createMockAssessmentService(): AssessmentService {
  return {
    async listForCustomer(customerId: string): Promise<Assessment[]> {
      await mockDelay(300)
      return mockAssessmentsForCustomer(customerId).map(a => ({ ...a }))
    },

    async listAuthorizedClientOwnedPage(clientCustomerId: number, _options = {}) {
      if (!Number.isSafeInteger(clientCustomerId) || clientCustomerId < 1) {
        throw new Error('A verified client-owned Customer ID is required')
      }
      const seededCustomerId = `cust-${String(clientCustomerId).padStart(3, '0')}`
      const customerId = mockAssessments.some(item => item.customerId === seededCustomerId)
        ? seededCustomerId
        : String(clientCustomerId)
      const items = await this.listForCustomer(customerId)
      return {
        items: items.map(
          ({
            id,
            sourceVersionId,
            customerId,
            financialYear,
            assessmentYear,
            itrType,
            status,
            dueDate,
            filedDate,
          }) => ({
            id,
            sourceVersionId,
            customerId,
            financialYear,
            assessmentYear,
            itrType,
            status,
            dueDate,
            ...(filedDate ? { filedDate } : {}),
          })
        ),
        nextCursor: null,
      }
    },

    async getAuthorizedClientOwnedDetail(
      clientCustomerId,
      item,
      options
    ): Promise<AuthorizedAssessmentDetail> {
      if (
        !Number.isSafeInteger(clientCustomerId) ||
        clientCustomerId < 1 ||
        !item.sourceVersionId
      ) {
        throw new Error('An exact saved assessment version and verified client ID are required')
      }
      await mockDelay(250)
      return {
        id: item.id,
        sourceVersionId: item.sourceVersionId,
        customerId: clientCustomerId,
        customerType: options.customerType,
        financialYear: item.financialYear,
        assessmentYear: item.assessmentYear,
        itrType: item.itrType,
        clientData: {
          pan: item.pan ?? 'ABCDE1234F',
          taxRegime: item.taxRegime ?? 'new',
          status: item.status,
          dueDate: item.dueDate,
          grossTotalIncome: String(item.grossTotalIncome ?? 0),
          totalDeductions: String(item.totalDeductions ?? 0),
          taxableIncome: String(item.taxableIncome ?? 0),
          totalTaxLiability: String(item.totalTaxLiability ?? 0),
          totalTaxPaid: String(item.taxPaid ?? 0),
          taxPayable: String(item.demandAmount ?? 0),
          refundAmount: String(item.refundAmount ?? 0),
          demandAmount: String(item.demandAmount ?? 0),
        },
        formDataStatus: 'withheld_pending_schema',
        originFirmWorkProduct:
          options.sourceMode === 'retained'
            ? { assignedToName: null, riskLevel: 'standard', workValue: '0' }
            : null,
      }
    },

    async get(id: string): Promise<Assessment> {
      await mockDelay(200)
      const found = mockAssessments.find(a => a.id === id)
      if (!found) throw new Error('Assessment not found')
      return { ...found }
    },

    async create(input: CreateAssessmentInput): Promise<Assessment> {
      await mockDelay(500)
      const now = new Date().toISOString()
      const created: Assessment = {
        id: `asmt-${Date.now()}`,
        customerId: input.customerId,
        pan: input.pan,
        financialYear: input.financialYear,
        assessmentYear: input.assessmentYear,
        itrType: input.itrType,
        taxRegime: input.taxRegime ?? 'new',
        status: 'not_started',
        dueDate: input.dueDate ?? `${input.assessmentYear.split('-')[0]}-07-31`,
        totalDeductions: input.totalDeductions,
        createdDate: now,
        lastUpdated: now,
      }
      mockAssessments.unshift(created)
      return { ...created }
    },

    async update(id: string, patch: UpdateAssessmentInput): Promise<Assessment> {
      await mockDelay(400)
      const index = mockAssessments.findIndex(a => a.id === id)
      const now = new Date().toISOString()
      if (index !== -1) {
        mockAssessments[index] = { ...mockAssessments[index], ...patch, lastUpdated: now }
        return { ...mockAssessments[index] }
      }
      const merged: Assessment = {
        id,
        customerId: 'unknown',
        financialYear: '2024-25',
        assessmentYear: '2025-26',
        itrType: 'ITR-1',
        status: 'not_started',
        dueDate: '2025-07-31',
        ...patch,
        lastUpdated: now,
      }
      mockAssessments.unshift(merged)
      return merged
    },

    async updateStatus(id: string, status: AssessmentStatus): Promise<Assessment> {
      await mockDelay(300)
      const index = mockAssessments.findIndex(a => a.id === id)
      const now = new Date().toISOString()
      if (index !== -1) {
        mockAssessments[index] = { ...mockAssessments[index], status, lastUpdated: now }
        return { ...mockAssessments[index] }
      }
      const merged: Assessment = {
        id,
        customerId: 'unknown',
        financialYear: '2024-25',
        assessmentYear: '2025-26',
        itrType: 'ITR-1',
        status,
        dueDate: '2025-07-31',
        lastUpdated: now,
      }
      mockAssessments.unshift(merged)
      return merged
    },

    async calculateTax(input: TaxCalculationInput): Promise<TaxCalculationResult> {
      await mockDelay(400)
      const regime = input.taxRegime ?? 'new'
      const computed = computeTax(input.grossIncome, input.totalDeductions ?? 0, regime)
      return {
        ...computed,
        taxRegime: regime,
        assessmentYear: input.assessmentYear,
      }
    },

    async listDocuments(assessmentId: string): Promise<AssessmentDocument[]> {
      await mockDelay(200)
      return mockAssessmentDocuments.filter(d => d.assessmentId === assessmentId)
    },

    async linkDocument(
      assessmentId: string,
      input: LinkAssessmentDocumentInput
    ): Promise<AssessmentDocument> {
      await mockDelay(300)
      const newDoc: AssessmentDocument = {
        id: `asmt-doc-${Date.now()}`,
        assessmentId,
        assessmentType: 'tax',
        documentId: input.documentId,
        category: input.category,
        displayName: input.displayName,
        isMandatory: input.isMandatory ?? false,
        isReceived: true,
        isVerified: false,
        linkedAt: new Date().toISOString(),
      }
      mockAssessmentDocuments.unshift(newDoc)
      return { ...newDoc }
    },

    async unlinkDocument(assessmentId: string, documentId: string): Promise<void> {
      await mockDelay(200)
      const idx = mockAssessmentDocuments.findIndex(
        d => d.assessmentId === assessmentId && d.documentId === documentId
      )
      if (idx !== -1) {
        mockAssessmentDocuments.splice(idx, 1)
      }
    },
  }
}
